import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { to: "/#ueber-uns", label: "Über uns", id: "ueber-uns" },
    { to: "/#leistungen", label: "Leistungen", id: "leistungen" },
    { to: "/#warum", label: "Warum wir", id: "warum" },
    { to: "/referenzen", label: "Referenzen", id: "referenzen", isRoute: true },
    { to: "/kontakt", label: "Kontakt", id: "kontakt", isRoute: true },
];

const Logo = () => (
    <Link to="/" data-testid="nav-logo" className="flex items-center">
        <img
            src="/cler-bau-logo.png"
            alt="CLER BAU AG"
            className="h-7 lg:h-9 w-auto select-none"
            draggable={false}
        />
    </Link>
);

// Smart link: if it's a hash link and we're already on home, use anchor + smooth scroll
function NavLink({ item, onClick, className }) {
    const location = useLocation();
    const isHome = location.pathname === "/";

    if (item.isRoute) {
        return (
            <Link
                to={item.to}
                data-testid={`nav-link-${item.id}`}
                className={className}
                onClick={onClick}
            >
                {item.label}
            </Link>
        );
    }

    // Hash link
    if (isHome) {
        const anchor = item.to.replace("/#", "#");
        return (
            <a
                href={anchor}
                data-testid={`nav-link-${item.id}`}
                className={className}
                onClick={onClick}
            >
                {item.label}
            </a>
        );
    }

    return (
        <Link
            to={item.to}
            data-testid={`nav-link-${item.id}`}
            className={className}
            onClick={onClick}
        >
            {item.label}
        </Link>
    );
}

export default function Navigation() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const ctaItem = { to: "/kontakt", id: "cta", label: "Offerte", isRoute: true };

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-[background,border-color,backdrop-filter] duration-300 ${
                scrolled
                    ? "bg-[#FEFEFE]/95 border-b border-[rgba(20,16,13,0.08)] backdrop-blur"
                    : "bg-[#FEFEFE]/60 border-b border-transparent backdrop-blur-sm"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
                <Logo />

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.id}
                            item={l}
                            className="relative text-[13px] tracking-[0.14em] uppercase text-[#14100D] transition-colors duration-300 hover:text-[#E20514]"
                        />
                    ))}
                </nav>

                <NavLink
                    item={ctaItem}
                    className="hidden md:inline-flex btn-primary px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase font-semibold"
                />

                <button
                    onClick={() => setOpen((s) => !s)}
                    className="md:hidden text-[#14100D] p-2"
                    aria-label="Menü öffnen"
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-[#FEFEFE] border-t border-[rgba(20,16,13,0.08)]" data-testid="nav-mobile">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <NavLink
                                key={l.id}
                                item={l}
                                onClick={() => setOpen(false)}
                                className="text-[15px] tracking-[0.14em] uppercase text-[#14100D] hover:text-[#E20514] transition-colors"
                            />
                        ))}
                        <NavLink
                            item={{ ...ctaItem, label: "Offerte anfragen" }}
                            onClick={() => setOpen(false)}
                            className="btn-primary px-5 py-3 text-center text-[12px] tracking-[0.2em] uppercase font-semibold"
                        />
                    </div>
                </div>
            )}
        </header>
    );
}
